import { AdminInput, AdminTextarea, AdminToggle, AdminSectionCard } from './AdminFormInput';
import type { TemplateConfig } from '@/mocks/template-config';

interface InstitutionalFormProps {
  config: TemplateConfig['institutional'];
  onChange: (config: TemplateConfig['institutional']) => void;
}

export default function InstitutionalForm({ config, onChange }: InstitutionalFormProps) {
  const update = <K extends keyof TemplateConfig['institutional']>(key: K, value: TemplateConfig['institutional'][K]) => {
    onChange({ ...config, [key]: value });
  };

  const updateDifferential = (index: number, text: string) => {
    const newDiffs = [...config.differentials];
    newDiffs[index] = { ...newDiffs[index], text };
    update('differentials', newDiffs);
  };

  const toggleDifferential = (index: number) => {
    const newDiffs = [...config.differentials];
    newDiffs[index] = { ...newDiffs[index], show: !newDiffs[index].show };
    update('differentials', newDiffs);
  };

  const addDifferential = () => {
    update('differentials', [...config.differentials, { text: 'Novo diferencial', show: true }]);
  };

  const removeDifferential = (index: number) => {
    const newDiffs = config.differentials.filter((_, i) => i !== index);
    update('differentials', newDiffs);
  };

  return (
    <div className="space-y-5">
      <AdminSectionCard title="Bloco Institucional" icon="ri-article-line">
        <AdminToggle
          label="Mostrar Bloco Institucional"
          checked={config.show}
          onChange={(v) => update('show', v)}
        />
        <AdminTextarea
          label="Texto Principal"
          value={config.text}
          onChange={(e) => update('text', e.target.value)}
          rows={3}
        />
        <AdminInput
          label="Ano de Fundação"
          type="number"
          value={config.foundedYear}
          onChange={(e) => update('foundedYear', parseInt(e.target.value, 10))}
        />
        <div className="space-y-3">
          <label className="text-brand-text-muted text-xs font-medium uppercase tracking-wider block">
            Diferenciais
          </label>
          {config.differentials.map((diff, index) => (
            <div key={index} className="flex items-center gap-3">
              <input
                type="text"
                value={diff.text}
                onChange={(e) => updateDifferential(index, e.target.value)}
                className="flex-1 bg-brand-dark/60 border border-brand-gold/20 rounded-md py-2.5 px-3 text-brand-text text-sm focus:outline-none focus:border-brand-gold/50"
              />
              <button
                onClick={() => toggleDifferential(index)}
                className={`w-8 h-8 flex items-center justify-center rounded-md border transition-colors cursor-pointer ${
                  diff.show ? 'bg-brand-gold/20 border-brand-gold/40 text-brand-gold' : 'bg-brand-dark/40 border-brand-gold/10 text-brand-text-muted'
                }`}
                title={diff.show ? 'Ativo' : 'Oculto'}
              >
                <i className={`${diff.show ? 'ri-eye-line' : 'ri-eye-off-line'} text-sm`} />
              </button>
              <button
                onClick={() => removeDifferential(index)}
                className="w-8 h-8 flex items-center justify-center rounded-md border border-red-500/20 text-red-400 hover:bg-red-500/10 transition-colors cursor-pointer"
                title="Remover"
              >
                <i className="ri-delete-bin-line text-sm" />
              </button>
            </div>
          ))}
          <button
            onClick={addDifferential}
            className="flex items-center gap-2 text-brand-gold hover:text-brand-gold-light text-sm transition-colors cursor-pointer"
          >
            <i className="ri-add-line" />
            Adicionar Diferencial
          </button>
        </div>
        <div className="space-y-3 pt-2">
          <AdminToggle
            label="Mostrar Diferencial Personalizado"
            checked={config.showCustomDifferential}
            onChange={(v) => update('showCustomDifferential', v)}
          />
          {config.showCustomDifferential && (
            <AdminInput
              label="Texto do Diferencial Personalizado"
              value={config.customDifferential}
              onChange={(e) => update('customDifferential', e.target.value)}
            />
          )}
        </div>
      </AdminSectionCard>
    </div>
  );
}